import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Phone, MessageSquare, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Btn, Chip } from "@/components/brand";
import { useLoad } from "@/components/app";

const TYPE = {
  calendly: ["bg-[#F7ECCB] text-[#6B4E00]", "Calendly"], callRequest: ["bg-[#F6E2E3] text-[#8A1F2A]", "Call request"], callback: ["bg-[#F6E2E3] text-[#8A1F2A]", "Callback"],
  submitBy: ["bg-ink text-white", "Submit by"], resultsDue: ["bg-[#E4EFE4] text-[#1F5C33]", "Results"], custom: ["bg-tan text-ink", "Event"],
};
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const timeOf = (s) => (s && s.length > 10 ? new Date(s).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "");

function EventModal({ e, onClose, reload, calendlyLink }) {
  const [at, setAt] = useState("");
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); onClose(); } catch (x) { toast.error(errMsg(x)); } };
  return (
    <div className="fixed inset-0 z-50 bg-ink/60 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md p-6 border-t-4 border-gold" onClick={(x) => x.stopPropagation()} data-testid="event-modal">
        <div className="flex justify-between items-start gap-3">
          <div><span className={`chip ${TYPE[e.type]?.[0]}`}>{TYPE[e.type]?.[1]}</span><h3 className="f-display text-2xl mt-3">{e.title}</h3></div>
          <button onClick={onClose} aria-label="Close" className="w-11 h-11 flex items-center justify-center"><X /></button>
        </div>
        <p className="text-sm text-muted7 mt-2">{fmtDate(e.start.slice(0, 10), { weekday: "long", month: "long", day: "numeric" })} {timeOf(e.start)}</p>
        {e.detail && <p className="text-sm mt-2">{e.detail}</p>}
        {e.status === "canceled" && <Chip tone="neg" className="mt-2">Canceled</Chip>}
        <div className="flex flex-wrap gap-2 mt-5">
          {e.phone && <a href={`tel:${e.phone}`} className="btn btn-outline btn-sm"><Phone className="w-4 h-4" />Call</a>}
          {e.phone && <a href={`sms:${e.phone}`} className="btn btn-outline btn-sm"><MessageSquare className="w-4 h-4" />Text</a>}
          {e.clientId && <Link to={`/owner/clients/${e.clientId}`} className="btn btn-black btn-sm">Open client</Link>}
          {e.type === "submitBy" && <button className="btn btn-black btn-sm" onClick={() => act(() => api.post(`/owner/clients/${e.clientId}/mark-submitted`), "Marked submitted")} data-testid="event-mark-submitted">Mark submitted</button>}
          {e.type === "callRequest" && <button className="btn btn-gold btn-sm" onClick={() => act(() => api.patch(`/owner/call-requests/${e.callRequestId}`, { status: "done" }), "Call marked done")} data-testid="event-call-done">Mark call done</button>}
          {e.type === "callback" && <Link to="/owner/consults" className="btn btn-gold btn-sm">Open consult</Link>}
          {(e.type === "calendly") && <a href={e.rescheduleUrl || calendlyLink || "https://calendly.com/app/scheduled_events"} target="_blank" rel="noopener noreferrer" className="btn btn-outline btn-sm">Reschedule ↗</a>}
        </div>
        {e.type === "callRequest" && (
          <div className="mt-5 pt-4 border-t border-tanline">
            <label className="label">Schedule this call</label>
            <div className="flex gap-2"><input type="datetime-local" className="field" value={at} onChange={(x) => setAt(x.target.value)} data-testid="schedule-call-input" />
              <button className="btn btn-black btn-sm" disabled={!at} onClick={() => act(() => api.patch(`/owner/call-requests/${e.callRequestId}`, { scheduledAt: at }), "Scheduled — client notified")} data-testid="schedule-call-btn">Confirm</button></div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function OwnerCalendar() {
  const [cur, setCur] = useState(new Date());
  const [view, setView] = useState("month");
  const [sel, setSel] = useState(null);
  const range = useMemo(() => {
    const s = new Date(cur.getFullYear(), cur.getMonth(), 1);
    s.setDate(s.getDate() - s.getDay());
    const e = new Date(s);
    e.setDate(e.getDate() + 41);
    return [iso(s), iso(e), s];
  }, [cur]);
  const { data, reload } = useLoad(`/owner/calendar?start=${range[0]}&end=${range[1]}`);
  const { data: up } = useLoad("/owner/calendar/upcoming", [data]);
  const events = data?.events || [];
  const byDay = (d) => events.filter((e) => e.start.slice(0, 10) === d);
  const today = iso(new Date());
  const shift = (n) => { const d = new Date(cur); if (view === "month") d.setMonth(d.getMonth() + n); else d.setDate(d.getDate() + n * (view === "week" ? 7 : 1)); setCur(d); };
  const days = view === "month" ? Array.from({ length: 42 }, (_, i) => { const d = new Date(range[2]); d.setDate(d.getDate() + i); return d; })
    : view === "week" ? Array.from({ length: 7 }, (_, i) => { const d = new Date(cur); d.setDate(d.getDate() - d.getDay() + i); return d; }) : [cur];
  const Ev = ({ e }) => (
    <button onClick={() => setSel(e)} className={`w-full text-left text-[11px] px-1.5 py-1 truncate ${TYPE[e.type]?.[0]} ${e.status === "canceled" ? "line-through opacity-60" : ""}`} data-testid={`cal-event-${e.type}`}>
      {timeOf(e.start) && <b className="mr-1">{timeOf(e.start)}</b>}{e.title}
    </button>
  );
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="f-display text-4xl">Calendar</h1>
        <div className="flex flex-wrap items-center gap-2">
          <Chip tone={data?.calendlyConnected ? "ok" : "line"} data-testid="calendly-sync-chip">{data?.calendlyConnected ? "● Synced with Calendly" : "Calendly not connected"}</Chip>
          {data?.calendlyConnected && <button className="btn btn-outline btn-sm" onClick={() => api.post("/owner/calendly/sync").then((r) => { toast.success(`Synced ${r.data.synced} bookings`); reload(); }).catch((e) => toast.error(errMsg(e)))} data-testid="calendly-sync-btn">Sync now</button>}
          <Btn as="a" href={data?.calendlyLink || "https://calendly.com/app/scheduled_events"} target="_blank" rel="noopener noreferrer" variant="gold" arrow className="btn-sm" data-testid="open-calendly-btn">Open Calendly</Btn>
        </div>
      </div>
      <div className="grid xl:grid-cols-[1fr_300px] gap-5">
        <div className="bg-white border border-tanline">
          <div className="flex flex-wrap items-center justify-between gap-3 p-4">
            <div className="flex items-center gap-2">
              <button className="w-11 h-11 border border-tanline flex items-center justify-center" onClick={() => shift(-1)} aria-label="Previous" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
              <button className="w-11 h-11 border border-tanline flex items-center justify-center" onClick={() => shift(1)} aria-label="Next" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
              <button className="btn btn-outline btn-sm" onClick={() => setCur(new Date())}>Today</button>
              <h2 className="f-display text-2xl ml-2">{cur.toLocaleDateString("en-US", { month: "long", year: "numeric", ...(view === "day" ? { day: "numeric" } : {}) })}</h2>
            </div>
            <div className="flex">{["month", "week", "day"].map((v) => <button key={v} onClick={() => setView(v)} className={`btn btn-sm !min-h-[36px] ${view === v ? "btn-black" : "btn-outline"}`} data-testid={`cal-view-${v}`}>{v}</button>)}</div>
          </div>
          {view === "month" ? (
            <div className="overflow-x-auto">
              <div className="min-w-[700px]">
                <div className="grid grid-cols-7 bg-ink">{["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"].map((d) => <div key={d} className="f-mono text-[10px] tracking-[0.16em] text-gold py-2.5 text-center">{d}</div>)}</div>
                <div className="grid grid-cols-7">
                  {days.map((d) => {
                    const k = iso(d);
                    const isT = k === today;
                    const dim = d.getMonth() !== cur.getMonth();
                    return (
                      <div key={k} className={`min-h-[108px] border-r border-b border-tanline p-1.5 space-y-1 ${isT ? "bg-[#FBF3DC] ring-1 ring-inset ring-gold" : ""} ${dim ? "bg-cream/60" : ""}`} data-testid={isT ? "cal-today" : undefined}>
                        <p className={`f-mono text-[11px] ${dim ? "text-muted7/50" : ""} ${isT ? "font-bold text-goldtext" : ""}`}>{d.getDate()}</p>
                        {byDay(k).slice(0, 3).map((e) => <Ev key={e.id} e={e} />)}
                        {byDay(k).length > 3 && <button className="text-[10px] text-muted7" onClick={() => { setCur(d); setView("day"); }}>+{byDay(k).length - 3} more</button>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-tanline border-t border-tanline">
              {days.map((d) => (
                <div key={iso(d)} className={`p-4 flex gap-4 ${iso(d) === today ? "bg-[#FBF3DC]" : ""}`}>
                  <div className="w-16 shrink-0"><p className="f-mono text-[10px] text-muted7">{d.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase()}</p><p className="f-display text-3xl">{d.getDate()}</p></div>
                  <div className="flex-1 space-y-1.5">{byDay(iso(d)).length ? byDay(iso(d)).map((e) => <Ev key={e.id} e={e} />) : <p className="text-sm text-muted7 py-2">Nothing scheduled.</p>}</div>
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-3 p-4 border-t border-tanline">{Object.entries(TYPE).map(([k, [c, l]]) => <span key={k} className={`chip ${c}`}>{l}</span>)}</div>
        </div>
        <aside className="bg-white border border-tanline p-5 h-fit" data-testid="coming-up">
          <h2 className="f-display text-2xl">Coming up</h2>
          <ul className="mt-4 space-y-3">
            {(up || []).map((e) => (
              <li key={e.id}><button onClick={() => setSel(e)} className="w-full flex gap-3 text-left">
                <span className="w-12 shrink-0 bg-ink text-white text-center py-1.5"><span className="block f-mono text-[9px] text-gold">{fmtDate(e.start.slice(0, 10), { month: "short" }).toUpperCase()}</span><span className="f-display text-xl leading-none">{Number(e.start.slice(8, 10))}</span></span>
                <span className="min-w-0"><span className={`chip ${TYPE[e.type]?.[0]} !text-[9px]`}>{TYPE[e.type]?.[1]}</span><span className="block text-sm mt-1 truncate">{e.title}</span></span>
              </button></li>
            ))}
            {up && !up.length && <li className="text-sm text-muted7">Nothing coming up.</li>}
          </ul>
        </aside>
      </div>
      {sel && <EventModal e={sel} onClose={() => setSel(null)} reload={reload} calendlyLink={data?.calendlyLink} />}
    </div>
  );
}
